'use client'

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { api, useAuth } from './AuthContext'

const CartContext = createContext(undefined)

export const useCart = () => {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within CartProvider')
  return ctx
}

export const CartProvider = ({ children }) => {
  const { token } = useAuth()
  const [cartCount, setCartCount] = useState(0)
  const isFetching = useRef(false)
  const mountedRef = useRef(true)

  const refreshCartCount = useCallback(async () => {
    if (!token || !mountedRef.current) {
      setCartCount(0)
      return
    }
    
    // Prevent multiple simultaneous requests
    if (isFetching.current) {
      console.log('Cart refresh already in progress, skipping')
      return
    }
    
    isFetching.current = true
    try {
      const res = await api.get('/cart')
      const count =
        res.data.items?.reduce((sum, item) => sum + item.quantity, 0) || 0
      if (mountedRef.current) {
        setCartCount(count)
      }
    } catch (error) {
      console.error('Failed to refresh cart count:', error)
      if (mountedRef.current) {
        setCartCount(0)
      }
    } finally {
      if (mountedRef.current) {
        isFetching.current = false
      }
    }
  }, [token])

  useEffect(() => {
    mountedRef.current = true
    refreshCartCount()
    
    return () => {
      mountedRef.current = false
    }
  }, [token]) // Only depend on token, not refreshCartCount

  return (
    <CartContext.Provider value={{ cartCount, refreshCartCount }}>
      {children}
    </CartContext.Provider>
  )
}
